'use client';

import React, { useEffect, useRef } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Typography } from '@mui/material';

interface LineSelectorProps {
  allLines: (string | number)[];
  selectedLine?: string | number;
  onLineSelect: (line: string | number) => void;
  /** Same length as `allLines`: label shown for each slot (e.g. abs handicap when values are market ids). */
  lineDisplayLabels?: (string | number)[];
}

interface LineItemProps {
  line: string | number;
  displayLabel?: string | number;
  isSelected: boolean;
  onLineSelect: (line: string | number) => void;
}

const LineItem = ({ line, displayLabel, isSelected, onLineSelect }: LineItemProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLineSelect(line);
  };

  return (
    <Box
      onClick={handleClick}
      data-selected={isSelected}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        minWidth: 'fit-content',
      }}
    >
      <Box
        sx={{ height: '6px', mb: '2px', display: 'flex', justifyContent: 'center', width: '100%' }}
      >
        {isSelected && (
          <Box
            sx={{
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '6px solid #2563eb', // Downward pointing caret
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '0.8125rem',
          fontWeight: isSelected ? 700 : 500,
          color: isSelected ? 'primary.main' : 'text.secondary',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          px: 1.5,
          py: 0.5,
          borderRadius: '12px',
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: 'action.hover',
            color: 'text.primary',
          },
        }}
      >
        {displayLabel !== undefined && displayLabel !== null && String(displayLabel) !== ''
          ? displayLabel
          : Math.abs(Number(line))}
      </Typography>
    </Box>
  );
};

export const LineSelector = ({
  allLines,
  selectedLine,
  onLineSelect,
  lineDisplayLabels,
}: LineSelectorProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleAdjacentSelect = (direction: 'left' | 'right') => {
    if (selectedLine === undefined || selectedLine === null) return;
    const currentIndex = allLines.findIndex((l) => String(l) === String(selectedLine));
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    if (direction === 'left' && currentIndex > 0) {
      nextIndex = currentIndex - 1;
    } else if (direction === 'right' && currentIndex < allLines.length - 1) {
      nextIndex = currentIndex + 1;
    }

    if (nextIndex !== currentIndex) {
      onLineSelect(allLines[nextIndex]);
    }
  };

  const handleLeftClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleAdjacentSelect('left');
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleAdjacentSelect('right');
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const selectedElement =
        scrollRef.current?.querySelector<HTMLElement>('[data-selected="true"]');
      if (selectedElement && scrollRef.current) {
        const container = scrollRef.current;
        const scrollLeft =
          selectedElement.offsetLeft - container.clientWidth / 2 + selectedElement.clientWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }, 100);
    return () => clearTimeout(timeout);
  }, [selectedLine, allLines]);

  const isLeftDisabled =
    selectedLine === undefined ||
    allLines.findIndex((l) => String(l) === String(selectedLine)) <= 0;

  const isRightDisabled =
    !allLines ||
    allLines.length === 0 ||
    selectedLine === undefined ||
    allLines.findIndex((l) => String(l) === String(selectedLine)) >= allLines.length - 1;

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        position: 'relative',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <IconButton
        onClick={handleLeftClick}
        size="small"
        disabled={isLeftDisabled}
        sx={{
          bgcolor: 'transparent',
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
          '&.Mui-disabled': { opacity: 0.2 },
        }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          px: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            m: '0 auto',
            width: 'fit-content',
            minWidth: 'min-content',
            px: 2,
          }}
        >
          {allLines.map((line, idx) => (
            <LineItem
              key={`${String(line)}-${idx}`}
              line={line}
              displayLabel={lineDisplayLabels?.[idx]}
              isSelected={String(line) === String(selectedLine)}
              onLineSelect={onLineSelect}
            />
          ))}
        </Box>
      </Box>

      <IconButton
        onClick={handleRightClick}
        size="small"
        disabled={isRightDisabled}
        sx={{
          bgcolor: 'transparent',
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
          '&.Mui-disabled': { opacity: 0.2 },
        }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
